import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { SiGithub, SiLinkedin } from "react-icons/si";
import {
  IconArrowRight,
  IconBriefcase,
  IconGraduationCap,
  IconMail,
  IconMapPin,
  IconSparkles,
} from "../components/icons/ThinIcons";
import { useSiteConfig, useUiLabels } from "../context/LocaleContext";

const easeFluid = [0.32, 0.72, 0, 1] as const;

function About() {
  const config = useSiteConfig();
  const ui = useUiLabels();
  const reduceMotion = useReducedMotion();
  const storyParagraphs = config.aboutStory.split(/\n\n+/).filter(Boolean);

  const motionPage = reduceMotion
    ? { initial: false, animate: { opacity: 1 } }
    : {
        initial: { opacity: 0, y: 18 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.75, ease: easeFluid },
      };

  const quickIcons = [IconBriefcase, IconGraduationCap, IconMapPin];

  return (
    <motion.main
      {...motionPage}
      className="flex w-full min-h-0 justify-center px-4 sm:px-8"
    >
      <div className="py-24 sm:py-32 pb-28 w-full max-w-6xl">
        <header className="text-center max-w-3xl mx-auto mb-16 sm:mb-20">
          <p className="mb-5 inline-flex rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-emerald-200/90 ring-1 ring-emerald-400/20">
            {ui.about.badge}
          </p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white font-heading leading-tight mb-6 text-balance-safe">
            {config.aboutHeadline}
          </h1>
          <p className="text-base sm:text-lg text-zinc-400 font-body leading-relaxed">{config.aboutSubhead}</p>

          <div className="mt-10 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center gap-3 sm:gap-4">
            <Link
              to="/contact"
              className="group inline-flex items-center gap-0 rounded-full bg-gradient-to-r from-emerald-400/90 to-teal-500/90 px-6 py-3.5 text-sm font-semibold text-[#050505] transition-[transform,box-shadow] duration-700 ease-fluid active:scale-[0.98] min-h-[48px] shadow-[0_16px_50px_-28px_rgba(16,185,129,0.5)]"
            >
              <span>{ui.about.ctaProject}</span>
              <span className="ml-3 flex h-8 w-8 items-center justify-center rounded-full bg-black/10 transition-[transform] duration-700 ease-fluid group-hover:translate-x-0.5 group-hover:-translate-y-px">
                <IconArrowRight className="h-4 w-4" aria-hidden />
              </span>
            </Link>
            <a
              href={`mailto:${config.email}?subject=${encodeURIComponent(ui.about.mailSubject)}`}
              className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-medium text-zinc-100 ring-1 ring-white/[0.12] bg-white/[0.04] transition-[transform,background-color] duration-700 ease-fluid hover:bg-white/[0.07] active:scale-[0.98] min-h-[48px]"
            >
              <IconMail className="w-4 h-4 shrink-0 opacity-90" aria-hidden />
              Email
            </a>
            <a
              href={config.social.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-medium text-zinc-300 ring-1 ring-white/[0.08] transition-[transform,background-color] duration-700 ease-fluid hover:bg-white/[0.06] hover:text-white active:scale-[0.98] min-h-[48px]"
            >
              <SiLinkedin className="w-4 h-4 shrink-0 opacity-90" aria-hidden />
              LinkedIn
            </a>
            <a
              href={config.social.github}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-medium text-zinc-300 ring-1 ring-white/[0.08] transition-[transform,background-color] duration-700 ease-fluid hover:bg-white/[0.06] hover:text-white active:scale-[0.98] min-h-[48px]"
            >
              <SiGithub className="w-4 h-4 shrink-0 opacity-90" aria-hidden />
              GitHub
            </a>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-5 flex flex-col items-center lg:items-start">
            <div className="relative mb-10 w-full max-w-[280px] sm:max-w-xs mx-auto lg:mx-0">
              <div
                className="absolute -inset-2 rounded-full bg-gradient-to-br from-violet-500/25 via-emerald-400/15 to-amber-400/10 opacity-80 blur-xl"
                aria-hidden
              />
              <div className="bezel-outer relative">
                <div className="bezel-inner overflow-hidden p-1">
                  <img
                    src="/assets/ebuza3.jpg"
                    alt={`${ui.about.portraitAlt} ${config.name} ${config.surname}, ${config.role}`}
                    className="w-full aspect-square rounded-[calc(2rem-10px)] object-cover"
                    width={280}
                    height={280}
                  />
                </div>
              </div>
            </div>

            <div className="w-full bezel-outer">
              <div className="bezel-inner p-6 sm:p-7 text-white">
                <h2 className="text-[10px] font-semibold uppercase tracking-[0.2em] text-violet-200/85 mb-5">{ui.about.summaryHeading}</h2>
                <ul className="space-y-5">
                  {config.aboutQuickFacts.map((fact, i) => {
                    const Icon = quickIcons[i] ?? IconBriefcase;
                    return (
                      <li key={fact.label} className="flex gap-3 text-left">
                        <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/[0.06] ring-1 ring-white/[0.08] text-emerald-200">
                          <Icon className="w-4 h-4" aria-hidden />
                        </span>
                        <div>
                          <p className="text-[11px] font-medium uppercase tracking-wide text-zinc-500">{fact.label}</p>
                          <p className="text-sm font-body text-zinc-200 leading-snug">{fact.value}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 space-y-10">
            <section className="bezel-outer" aria-labelledby="about-story-heading">
              <div className="bezel-inner p-6 sm:p-9">
                <h2 id="about-story-heading" className="text-xl sm:text-2xl font-heading font-bold text-white mb-6">
                  {ui.about.storyHeading}
                </h2>
                <div className="space-y-4 text-sm sm:text-base font-body leading-relaxed text-zinc-400">
                  {storyParagraphs.map((para, idx) => (
                    <p key={idx}>{para}</p>
                  ))}
                </div>
              </div>
            </section>

            <section aria-labelledby="about-values-heading">
              <h2 id="about-values-heading" className="sr-only">
                {ui.about.valuesSrOnly}
              </h2>
              <ul className="grid sm:grid-cols-1 md:grid-cols-3 gap-4">
                {config.aboutValueProps.map((item) => (
                  <li key={item.title} className="bezel-outer h-full">
                    <div className="bezel-inner flex h-full flex-col p-5">
                      <IconSparkles className="w-5 h-5 text-emerald-300/90 mb-3" aria-hidden />
                      <h3 className="text-sm font-heading font-bold text-white mb-2 leading-snug">{item.title}</h3>
                      <p className="text-xs sm:text-sm font-body text-zinc-400 leading-relaxed flex-1">{item.description}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </section>

            <aside className="bezel-outer overflow-hidden">
              <div className="bezel-inner bg-gradient-to-br from-emerald-500/[0.08] to-transparent p-6 sm:p-8 text-center md:text-left">
                <p className="text-sm sm:text-base font-body text-zinc-300 leading-relaxed mb-8">{config.aboutAvailability}</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                  <Link
                    to="/contact"
                    className="group inline-flex items-center justify-center gap-0 rounded-full bg-white px-6 py-3 text-sm font-semibold text-[#050505] transition-[transform] duration-700 ease-fluid hover:bg-zinc-100 active:scale-[0.98] min-h-[48px]"
                  >
                    <span>{ui.about.formCta}</span>
                    <span className="ml-2 flex h-8 w-8 items-center justify-center rounded-full bg-black/5 transition-[transform] duration-700 ease-fluid group-hover:translate-x-0.5">
                      <IconArrowRight className="w-4 h-4" aria-hidden />
                    </span>
                  </Link>
                  <a
                    href={`mailto:${config.email}`}
                    className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-medium text-white ring-1 ring-white/[0.14] transition-[transform,background-color] duration-700 ease-fluid hover:bg-white/[0.06] min-h-[48px] active:scale-[0.98]"
                  >
                    <IconMail className="w-4 h-4" aria-hidden />
                    {config.email}
                  </a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </motion.main>
  );
}

export default About;
